import { type FC, useCallback, useContext } from "react";

import { css, cx } from "@emotion/css";
import { Button, Popover, PopoverPlacementTypes } from "@snapchat/snap-design-system";
import { ExitIcon } from "@snapchat/snap-design-system-icons";

import { useSnapSSOAuthService } from "../../core/sso-auth";
import { UserInfoContext } from "../../shared/api/contexts";
import { Flexbox } from "../../shared/components/Flexbox.styles";
import { useToggleState } from "../../shared/hooks/useToggleState";
import { popoverStyle, truncate } from "../../shared/styles";
import { openWindow } from "../../shared/utils/browser";

import { UserIcon } from "./UserIcon";
import {
    AccountName,
    ContentInfo,
    CreatorLink,
    ExitSection,
    popoverStyleWithPadding,
} from "./UserProfileDropdown.styles";
import { ViewCreatorProfile } from "./messages.generated";

const userProfileDropdown = css`
    margin-left: 20px;
`;

interface UserProfileDropdownContent {
    pageUrl: string;
    avatarUrl: string;
    userDisplayName: string;
}

const UserProfileDropdownContent: FC<UserProfileDropdownContent> = ({ pageUrl, avatarUrl, userDisplayName }) => {
    const openCreatorProfile = useCallback(() => openWindow(pageUrl), [pageUrl]);

    const authService = useSnapSSOAuthService();

    const signOut = useCallback(() => {
        authService.logout().catch(() => {
            // TODO - Error handling
        });
    }, [authService]);

    return (
        <Flexbox alignItems="center" justifyContent="space-between">
            <Flexbox alignItems="center">
                <UserIcon avatarUrl={avatarUrl} />
                <Flexbox flexDirection="column" className={ContentInfo}>
                    <AccountName className={truncate}>{userDisplayName}</AccountName>
                    <CreatorLink>
                        <Button type={"link"} onClick={openCreatorProfile}>
                            <ViewCreatorProfile />
                        </Button>
                    </CreatorLink>
                </Flexbox>
            </Flexbox>
            <ExitSection onClick={signOut}>
                <ExitIcon />
            </ExitSection>
        </Flexbox>
    );
};

interface UserProfileDropdown {
    pageUrl: string;
    avatarUrl: string;
    userDisplayName: string;
}

const UserProfileDropdown: FC<UserProfileDropdown> = ({ pageUrl, avatarUrl, userDisplayName }) => {
    const [{ visible, onCancel }, showModal] = useToggleState();

    const onVisibleChange = useCallback(
        (nextVisible: boolean) => {
            nextVisible ? showModal() : onCancel();
        },
        [showModal, onCancel],
    );

    return (
        <Popover
            trigger="click"
            visible={visible}
            onVisibleChange={onVisibleChange}
            className={cx(popoverStyle, popoverStyleWithPadding)}
            placement={PopoverPlacementTypes.BOTTOM_RIGHT}
            key="test-id-di"
            content={
                <UserProfileDropdownContent pageUrl={pageUrl} avatarUrl={avatarUrl} userDisplayName={userDisplayName} />
            }
        >
            <div className={userProfileDropdown}>
                <UserIcon avatarUrl={avatarUrl} />
            </div>
        </Popover>
    );
};

export const UserProfileDropdownContainer: FC = () => {
    const userInfo = useContext(UserInfoContext);

    if (!!userInfo?.loading || !userInfo?.data) {
        return null;
    }

    const { pageUrl, avatarUrl, userDisplayName } = userInfo.data.getUserProfile;

    return <UserProfileDropdown pageUrl={pageUrl} avatarUrl={avatarUrl} userDisplayName={userDisplayName} />;
};
